import {
  Button,
  ButtonLink,
  CheckboxFieldController,
  SelectFieldController,
  Stack,
  TextInputFieldController,
} from '@fukuri-admins/ui/common';
import { CustomFieldFormController } from '~/features/EditClientForm/presentations/CustomFieldFormController';
import { CustomFieldDefinition } from '~/types/resources/CustomFieldDefinition';
import { getClientLink } from '~/utils/getLinks';
import styles from './styles.module.scss';

/** グループ選択のオプション */
type GroupOption = {
  id: string;
  name: string;
  code: string;
};

/** フィーチャーフラグのオプション */
type ClientFeatureFlagOption = {
  value: string;
  label: string;
  disabled: boolean;
};

type Props = {
  /** クライアントID */
  clientId: number;
  /** 企業名 */
  clientName: string;
  /** 保存ボタンの非活性状態 */
  isButtonDisabled: boolean;
  /** グループの選択肢 */
  groupOptions: GroupOption[];
  /** カスタムフィールド定義 */
  customFieldDefinitions: CustomFieldDefinition[];
  /** フィーチャーフラグの選択肢 */
  clientFeatureFlagOptions: ClientFeatureFlagOption[];
  /** フォーム送信時のハンドラ */
  onSubmit: React.ComponentProps<'form'>['onSubmit'];
};

/**
 * 利用企業編集フォームのプレゼンテーションコンポーネント（Legacy）
 */
export const LegacyEditClientPresentation: React.FC<Props> = ({
  clientId,
  clientName,
  isButtonDisabled,
  groupOptions,
  customFieldDefinitions,
  clientFeatureFlagOptions,
  onSubmit,
}) => (
  <form onSubmit={onSubmit} className={styles.form}>
    <Stack direction='column' gap={24}>
      <span className={styles.title}>{clientName}の編集</span>
      <TextInputFieldController
        name='name'
        fieldWrapperProps={{
          labelType: 'required',
          labelText: '会社名',
        }}
        textInput={{
          type: 'text',
          placeholder: '合同会社〇〇',
        }}
      />
      <TextInputFieldController
        name='contactPersonName'
        fieldWrapperProps={{
          labelType: 'required',
          labelText: '担当者',
        }}
        textInput={{
          type: 'text',
          placeholder: '山田 太郎',
        }}
      />
      <TextInputFieldController
        name='contactPersonEmail'
        fieldWrapperProps={{
          labelType: 'required',
          labelText: 'メールアドレス',
        }}
        textInput={{
          type: 'email',
          placeholder: 'xxx@xxxx.xx.xx',
        }}
      />
      <TextInputFieldController
        name='contactPersonPhone'
        fieldWrapperProps={{
          labelType: 'required',
          labelText: '電話番号',
        }}
        textInput={{
          type: 'tel',
          placeholder: '090-0000-0000',
        }}
      />
      <TextInputFieldController
        name='corporateNumber'
        fieldWrapperProps={{
          labelText: '法人番号',
        }}
        textInput={{
          type: 'text',
          placeholder: '任意の文字列を入力してください',
        }}
      />
      <TextInputFieldController
        name='employeeCount'
        fieldWrapperProps={{
          labelText: '利用予定者数',
          labelType: 'required',
        }}
        textInput={{
          type: 'text',
          placeholder: '利用予定者数を半角数字で入力してください',
          inputMode: 'numeric',
        }}
      />
      <TextInputFieldController
        name='externalClientId'
        fieldWrapperProps={{
          labelText: '外部企業ID',
        }}
        textInput={{
          type: 'text',
          placeholder: '任意の文字列を入力してください',
        }}
      />
      {groupOptions.length > 0 && (
        <SelectFieldController
          name='groupId'
          fieldWrapperProps={{
            labelText: 'グループ',
          }}
          select={{
            placeholder: '選択してください',
            options: groupOptions.map(group => ({
              value: group.id,
              label: (
                <div className={styles.selectOption}>
                  <span className={styles.selectOption__name}>{group.name}</span>
                  <span className={styles.selectOption__code}>（{group.code}）</span>
                </div>
              ),
            })),
            clearable: true,
          }}
        />
      )}
      {customFieldDefinitions.map(field => (
        <CustomFieldFormController
          key={field.id}
          name={`customFields.${field.label}`}
          field={field}
        />
      ))}
      {clientFeatureFlagOptions.length > 0 && (
        <CheckboxFieldController
          name='clientFeatureFlags'
          options={clientFeatureFlagOptions}
          fieldWrapperProps={{
            labelText: '',
          }}
        />
      )}
      <div className={styles.actions}>
        <Stack direction='row' gap={16}>
          <ButtonLink to={getClientLink(clientId)} variant='outline' size='md'>
            キャンセル
          </ButtonLink>
          <Button type='submit' size='md' disabled={isButtonDisabled}>
            保存
          </Button>
        </Stack>
      </div>
    </Stack>
  </form>
);
